import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Progress } from '../ui/progress';
import { 
  Users, Video, Crown, Calendar, Zap, Trash2, StopCircle, TrendingUp,
  PlayCircle, XCircle, RefreshCw, Trophy, Gamepad2, DollarSign,
  Award, Target, BarChart3, Clock, AlertTriangle, CheckCircle2,
  Plus, Send, Eye, Bell, Copy, Megaphone, Settings, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QuickActionsTab = ({ 
  stats, 
  submissions, 
  winners,
  challenges,
  users,
  token,
  handlePickWinner,
  handleEndChallenge,
  handleClearSubmissions,
  handleEndAllChallenges,
  handleDeleteEndedChallenges,
  onRefresh,
  onSwitchTab
}) => {
  const navigate = useNavigate();
  const [actionLoading, setActionLoading] = useState(null);

  const activeChallenges = challenges?.filter(c => c.status === 'active') || [];
  const endedChallenges = challenges?.filter(c => c.status === 'ended') || [];
  const scheduledChallenges = challenges?.filter(c => c.status === 'scheduled') || [];
  const totalPrizePool = activeChallenges.reduce((sum, c) => sum + (parseFloat(c.prize_amount) || 0), 0);
  const totalPrizeAwarded = winners?.reduce((sum, w) => sum + (parseFloat(w.prize_amount) || 0), 0) || 0;
  
  // Calculate analytics
  const bannedUsers = users?.filter(u => u.is_banned)?.length || 0;
  const activeUsers = (users?.length || 0) - bannedUsers;
  const avgSubmissionsPerChallenge = activeChallenges.length > 0 
    ? Math.round(submissions?.length / activeChallenges.length) 
    : 0;
  
  // Winners by place
  const firstPlaceWinners = winners?.filter(w => w.place === 1)?.length || 0;
  const secondPlaceWinners = winners?.filter(w => w.place === 2)?.length || 0;
  const thirdPlaceWinners = winners?.filter(w => w.place === 3)?.length || 0;

  // Recent activity (last 24h estimation based on data)
  const recentSubmissions = submissions?.slice(0, 5) || [];
  const recentWinners = winners?.slice(0, 3) || [];

  const handleRandomWinner = () => {
    const safeSubmissions = Array.isArray(submissions) ? submissions : [];
    const safeWinners = Array.isArray(winners) ? winners : [];
    const nonWinnerSubmissions = safeSubmissions.filter(s => !safeWinners.some(w => w.submission_id === s.id));
    const randomSubmission = nonWinnerSubmissions[Math.floor(Math.random() * nonWinnerSubmissions.length)];
    if (randomSubmission) {
      if (window.confirm(`Random pick: ${randomSubmission.display_name}. Pick as 1st place winner?`)) {
        handlePickWinner?.(randomSubmission.id, 1, false);
      }
    } else {
      toast.error('No eligible submissions to pick from');
    }
  };

  // Post draft winners publicly
  const handlePostWinners = async () => {
    const draftWinners = winners?.filter(w => !w.published) || [];
    if (draftWinners.length === 0) {
      toast.error('No draft winners to post');
      return;
    }
    
    if (!window.confirm(`Post ${draftWinners.length} draft winner(s) publicly?`)) return;
    
    setActionLoading('post');
    try {
      await axios.post(`${API}/admin/winners/publish-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Posted ${draftWinners.length} winner(s)!`);
      onRefresh?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post winners');
    } finally {
      setActionLoading(null);
    }
  };

  // Quick create new challenge - switch to challenges tab
  const handleQuickChallenge = () => {
    if (onSwitchTab) {
      onSwitchTab('challenges');
    }
  };

  // Copy winner announcement text
  const handleCopyAnnouncement = () => {
    const recentWinner = winners?.filter(w => w.published)?.[0];
    if (!recentWinner) {
      toast.error('No published winners to announce');
      return;
    }
    
    const text = `🏆 Congratulations to ${recentWinner.display_name}!\n\nThey won ${recentWinner.prize_currency === 'USD' ? '$' : ''}${recentWinner.prize_amount} in the "${recentWinner.challenge_title}" challenge!\n\n🎮 Submit your videos for a chance to win!\n#GamingChallenge #Winner`;
    
    navigator.clipboard.writeText(text);
    toast.success('Announcement copied to clipboard!');
  };

  // View public pages
  const handleViewPublic = (path) => {
    window.open(path, '_blank');
  };

  // Calculate draft winners count
  const draftWinnersCount = winners?.filter(w => !w.published)?.length || 0;
  const publishedWinnersCount = winners?.filter(w => w.published)?.length || 0;

  return (
    <div className="space-y-6">
      {/* Main Stats Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
        <Card className="glass-card border-blue-500/30 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats.total_users || 0}</p>
              <p className="text-xs text-gray-500">Total Users</p>
            </div>
          </div>
        </Card>
        
        <Card className="glass-card border-purple-500/30 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
              <Video className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats.total_submissions || 0}</p>
              <p className="text-xs text-gray-500">Submissions</p>
            </div>
          </div>
        </Card>
        
        <Card className="glass-card border-yellow-500/30 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
              <Crown className="w-5 h-5 text-yellow-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats.total_winners || 0}</p>
              <p className="text-xs text-gray-500">Winners</p>
            </div>
          </div>
        </Card>
        
        <Card className="glass-card border-green-500/30 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-400">${Math.round(totalPrizePool)}</p>
              <p className="text-xs text-gray-500">Active Pool</p>
            </div>
          </div>
        </Card>

        <Card className="glass-card border-emerald-500/30 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
              <Award className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-emerald-400">${Math.round(totalPrizeAwarded)}</p>
              <p className="text-xs text-gray-500">Awarded</p>
            </div>
          </div>
        </Card>

        <Card className="glass-card border-red-500/30 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{challenges?.length || 0}</p>
              <p className="text-xs text-gray-500">Challenges</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Challenge Status & Analytics Row */}
      <div className="grid lg:grid-cols-2 gap-4">
        {/* Challenge Status */}
        <Card className="glass-card border-red-900/30">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base" style={{ fontFamily: 'Orbitron, sans-serif' }}>
              <BarChart3 className="w-5 h-5 text-red-400" />
              Challenge Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Active */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-green-400 flex items-center gap-1">
                    <PlayCircle className="w-4 h-4" /> Active
                  </span>
                  <span className="text-sm font-bold text-green-400">{activeChallenges.length}</span>
                </div>
                <Progress value={activeChallenges.length * 20} className="h-2 bg-green-500/20" />
              </div>
              
              {/* Scheduled */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-yellow-400 flex items-center gap-1">
                    <Clock className="w-4 h-4" /> Scheduled
                  </span>
                  <span className="text-sm font-bold text-yellow-400">{scheduledChallenges.length}</span>
                </div>
                <Progress value={scheduledChallenges.length * 20} className="h-2 bg-yellow-500/20" />
              </div>
              
              {/* Ended */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-gray-400 flex items-center gap-1">
                    <StopCircle className="w-4 h-4" /> Ended
                  </span>
                  <span className="text-sm font-bold text-gray-400">{endedChallenges.length}</span>
                </div>
                <Progress value={endedChallenges.length * 10} className="h-2 bg-gray-500/20" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Winner Distribution */}
        <Card className="glass-card border-yellow-500/30">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base" style={{ fontFamily: 'Orbitron, sans-serif' }}>
              <Trophy className="w-5 h-5 text-yellow-400" />
              Winner Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/30">
                <span className="text-2xl">🥇</span>
                <p className="text-2xl font-bold text-yellow-400">{firstPlaceWinners}</p>
                <p className="text-xs text-gray-500">1st Place</p>
              </div>
              <div className="p-3 bg-gray-500/10 rounded-lg border border-gray-500/30">
                <span className="text-2xl">🥈</span>
                <p className="text-2xl font-bold text-gray-300">{secondPlaceWinners}</p>
                <p className="text-xs text-gray-500">2nd Place</p>
              </div>
              <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/30">
                <span className="text-2xl">🥉</span>
                <p className="text-2xl font-bold text-amber-500">{thirdPlaceWinners}</p>
                <p className="text-xs text-gray-500">3rd Place</p>
              </div>
            </div>
            <div className="mt-4 p-3 bg-black/30 rounded-lg">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-400">Avg per Challenge</span>
                <span className="font-bold text-white">{avgSubmissionsPerChallenge} submissions</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Active Challenges Detail */}
      {activeChallenges.length > 0 && (
        <Card className="glass-card border-green-500/30">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center justify-between text-base">
              <span className="flex items-center gap-2" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                <PlayCircle className="w-5 h-5 text-green-400" />
                Active Challenges
              </span>
              <span className="text-green-400 font-bold">${Math.round(totalPrizePool)} Total Pool</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {activeChallenges.map((challenge) => {
                const challengeWinners = winners?.filter(w => w.challenge_id === challenge.id)?.length || 0;
                const maxWinners = challenge.max_winners || 1;
                const progressPercent = (challengeWinners / maxWinners) * 100;
                
                return (
                  <div key={challenge.id} className="p-4 bg-green-500/10 rounded-lg border border-green-500/30">
                    <div className="flex items-start gap-3">
                      {challenge.game_cover_url && (
                        <img src={challenge.game_cover_url} alt="" className="w-12 h-16 object-cover rounded" />
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-white truncate">{challenge.title}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded">
                            ${challenge.prize_amount}
                          </span>
                          {challenge.game_name && (
                            <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded truncate">
                              {challenge.game_name}
                            </span>
                          )}
                        </div>
                        <div className="mt-2">
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className="text-gray-400">Winners</span>
                            <span className="text-white">{challengeWinners}/{maxWinners}</span>
                          </div>
                          <Progress value={progressPercent} className="h-1.5" />
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          {challenge.submission_count || 0} submissions
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <Card className="glass-card border-red-500/30">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base" style={{ fontFamily: 'Orbitron, sans-serif' }}>
            <Zap className="w-5 h-5 text-yellow-400" />
            Quick Actions
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Primary Actions Row */}
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Primary Actions</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Button 
                className="bg-green-600 hover:bg-green-700 h-auto py-3 flex-col relative"
                onClick={handleQuickChallenge}
              >
                <Plus className="w-5 h-5 mb-1" />
                <span className="text-sm">New Challenge</span>
              </Button>
              <Button 
                className={`h-auto py-3 flex-col relative ${draftWinnersCount > 0 ? 'bg-purple-600 hover:bg-purple-700' : 'bg-gray-600'}`}
                onClick={handlePostWinners}
                disabled={draftWinnersCount === 0 || actionLoading === 'post'}
              >
                {actionLoading === 'post' ? (
                  <RefreshCw className="w-5 h-5 mb-1 animate-spin" />
                ) : (
                  <Send className="w-5 h-5 mb-1" />
                )}
                <span className="text-sm">Post Winners</span>
                {draftWinnersCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                    {draftWinnersCount}
                  </span>
                )}
              </Button>
              <Button 
                className="bg-yellow-600 hover:bg-yellow-700 h-auto py-3 flex-col"
                onClick={handleRandomWinner}
                disabled={submissions?.length === 0}
              >
                <Crown className="w-5 h-5 mb-1" />
                <span className="text-sm">Random Winner</span>
              </Button>
              <Button 
                className="bg-blue-600 hover:bg-blue-700 h-auto py-3 flex-col"
                onClick={handleCopyAnnouncement}
                disabled={publishedWinnersCount === 0}
              >
                <Copy className="w-5 h-5 mb-1" />
                <span className="text-sm">Copy Announce</span>
              </Button>
            </div>
          </div>

          {/* View Public Pages */}
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">View Public Pages</p>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              <Button 
                variant="outline"
                size="sm"
                className="border-gray-700 h-auto py-2 flex-col text-xs hover:bg-white/10"
                onClick={() => handleViewPublic('/')}
              >
                <Gamepad2 className="w-4 h-4 mb-1 text-red-400" />
                Home
              </Button>
              <Button 
                variant="outline"
                size="sm"
                className="border-gray-700 h-auto py-2 flex-col text-xs hover:bg-white/10"
                onClick={() => handleViewPublic('/gallery')}
              >
                <Video className="w-4 h-4 mb-1 text-purple-400" />
                Gallery
              </Button>
              <Button 
                variant="outline"
                size="sm"
                className="border-gray-700 h-auto py-2 flex-col text-xs hover:bg-white/10"
                onClick={() => handleViewPublic('/winners')}
              >
                <Trophy className="w-4 h-4 mb-1 text-yellow-400" />
                Winners
              </Button>
              <Button 
                variant="outline"
                size="sm"
                className="border-gray-700 h-auto py-2 flex-col text-xs hover:bg-white/10"
                onClick={() => handleViewPublic('/leaderboard')}
              >
                <TrendingUp className="w-4 h-4 mb-1 text-green-400" />
                Leaderboard
              </Button>
              <Button 
                variant="outline"
                size="sm"
                className="border-gray-700 h-auto py-2 flex-col text-xs hover:bg-white/10"
                onClick={() => handleViewPublic('/history')}
              >
                <Calendar className="w-4 h-4 mb-1 text-blue-400" />
                History
              </Button>
              <Button 
                variant="outline"
                size="sm"
                className="border-gray-700 h-auto py-2 flex-col text-xs hover:bg-white/10"
                onClick={() => handleViewPublic('/submit')}
              >
                <ExternalLink className="w-4 h-4 mb-1 text-cyan-400" />
                Submit
              </Button>
            </div>
          </div>

          {/* Danger Zone */}
          <div>
            <p className="text-xs text-red-400 uppercase tracking-wider mb-2">Danger Zone</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <Button 
                variant="outline"
                size="sm"
                className="border-red-900/50 text-red-400 hover:bg-red-500/20 h-auto py-2"
                onClick={() => {
                  if (window.confirm(`End all ${activeChallenges.length} active challenges?`)) {
                    handleEndAllChallenges?.();
                  }
                }}
                disabled={activeChallenges.length === 0}
              >
                <StopCircle className="w-4 h-4 mr-2" />
                End All Active ({activeChallenges.length})
              </Button>
              <Button 
                variant="outline"
                size="sm"
                className="border-red-900/50 text-red-400 hover:bg-red-500/20 h-auto py-2"
                onClick={() => {
                  if (window.confirm('Clear all submissions? This cannot be undone.')) {
                    handleClearSubmissions?.();
                  }
                }}
                disabled={submissions?.length === 0}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear Submissions
              </Button>
              <Button 
                variant="outline"
                size="sm"
                className="border-gray-700 text-gray-400 hover:bg-gray-500/20 h-auto py-2"
                onClick={() => {
                  if (window.confirm(`Delete ${endedChallenges.length} ended challenges?`)) {
                    handleDeleteEndedChallenges?.();
                  }
                }}
                disabled={endedChallenges.length === 0}
              >
                <XCircle className="w-4 h-4 mr-2" />
                Clear Ended ({endedChallenges.length})
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recent Activity Row */}
      <div className="grid lg:grid-cols-2 gap-4">
        {/* Recent Submissions */}
        <Card className="glass-card border-purple-500/30">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center justify-between text-base">
              <span className="flex items-center gap-2" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                <Video className="w-5 h-5 text-purple-400" />
                Recent Submissions
              </span>
              <span className="text-xs text-gray-500">{submissions?.length || 0} total</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentSubmissions.length > 0 ? (
              <div className="space-y-2">
                {recentSubmissions.map((sub) => {
                  const isWinner = winners?.some(w => w.submission_id === sub.id);
                  return (
                    <div key={sub.id} className="flex items-center justify-between p-2 bg-black/30 rounded-lg">
                      <div className="flex items-center gap-2 min-w-0">
                        <img 
                          src={sub.profile_image_url || 'https://via.placeholder.com/32'} 
                          alt={sub.display_name}
                          className="w-8 h-8 rounded-full"
                        />
                        <div className="min-w-0">
                          <p className="font-medium text-sm truncate">{sub.display_name}</p>
                          <p className="text-xs text-gray-500 truncate">{sub.challenge_title || sub.video_title || 'Untitled'}</p>
                        </div>
                      </div>
                      {isWinner ? (
                        <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs rounded flex items-center gap-1">
                          <Crown className="w-3 h-3" /> Winner
                        </span>
                      ) : (
                        <Button
                          size="sm"
                          className="bg-yellow-500 hover:bg-yellow-600 text-black h-7 px-2"
                          onClick={() => handlePickWinner(sub.id, 1, false)}
                        >
                          <Crown className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">No submissions yet</p>
            )}
          </CardContent>
        </Card>

        {/* Recent Winners */}
        <Card className="glass-card border-yellow-500/30">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center justify-between text-base">
              <span className="flex items-center gap-2" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                <Crown className="w-5 h-5 text-yellow-400" />
                Recent Winners
              </span>
              <span className="text-xs text-gray-500">{winners?.length || 0} total</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentWinners.length > 0 ? (
              <div className="space-y-2">
                {recentWinners.map((winner) => (
                  <div key={winner.id} className="flex items-center justify-between p-2 bg-black/30 rounded-lg">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="relative">
                        <img 
                          src={winner.profile_image_url || 'https://via.placeholder.com/32'} 
                          alt={winner.display_name}
                          className="w-8 h-8 rounded-full"
                        />
                        <span className="absolute -top-1 -right-1 text-sm">
                          {winner.place === 1 ? '🥇' : winner.place === 2 ? '🥈' : '🥉'}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate">{winner.display_name}</p>
                        <p className="text-xs text-gray-500 truncate">{winner.challenge_title || winner.game_name || 'Challenge'}</p>
                      </div>
                    </div>
                    <span className="text-green-400 font-bold text-sm">${winner.prize_amount}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">No winners yet</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* User Stats */}
      <Card className="glass-card border-blue-500/30">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base" style={{ fontFamily: 'Orbitron, sans-serif' }}>
            <Users className="w-5 h-5 text-blue-400" />
            User Overview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="p-3 bg-blue-500/10 rounded-lg text-center">
              <CheckCircle2 className="w-6 h-6 text-green-400 mx-auto mb-1" />
              <p className="text-xl font-bold text-white">{activeUsers}</p>
              <p className="text-xs text-gray-500">Active Users</p>
            </div>
            <div className="p-3 bg-red-500/10 rounded-lg text-center">
              <AlertTriangle className="w-6 h-6 text-red-400 mx-auto mb-1" />
              <p className="text-xl font-bold text-red-400">{bannedUsers}</p>
              <p className="text-xs text-gray-500">Banned</p>
            </div>
            <div className="p-3 bg-purple-500/10 rounded-lg text-center">
              <Video className="w-6 h-6 text-purple-400 mx-auto mb-1" />
              <p className="text-xl font-bold text-white">{submissions?.length || 0}</p>
              <p className="text-xs text-gray-500">Total Submissions</p>
            </div>
            <div className="p-3 bg-yellow-500/10 rounded-lg text-center">
              <Target className="w-6 h-6 text-yellow-400 mx-auto mb-1" />
              <p className="text-xl font-bold text-white">
                {users?.length > 0 ? Math.round((submissions?.length / users.length) * 100) / 100 : 0}
              </p>
              <p className="text-xs text-gray-500">Subs/User</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default QuickActionsTab;
